import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { invoiceApi, emailApi, agreementApi } from "@/services/api";
import { downloadInvoicePDF, printInvoicePDF } from "@/utils/pdfGenerator";
import { InvoiceHeader } from "@/components/invoice/InvoiceHeader";
import { InvoiceDetails } from "@/components/invoice/InvoiceDetails";
import { InvoiceLineItems } from "@/components/invoice/InvoiceLineItems";
import { InvoiceTotals } from "@/components/invoice/InvoiceTotals";
import { InvoiceFooter } from "@/components/invoice/InvoiceFooter";
import { InvoiceActions } from "@/components/invoice/InvoiceActions";
import { AppHeader } from "@/components/layout/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, FileX, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const TAX_RATE = 0.13;

export default function InvoicePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [companyInfo, setCompanyInfo] = useState({});
  const [customerInfo, setCustomerInfo] = useState({});
  const [invoiceInfo, setInvoiceInfo] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('Make cheque payable to "1001437133 ONTARIO INC."');
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [agreements, setAgreements] = useState([]);
  
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Fetch agreements for dropdown
  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        const data = await agreementApi.getAgreements();
        setAgreements(data.agreements || []);
      } catch (err) {
        console.error('Error fetching agreements:', err);
      }
    };
    fetchAgreements();
  }, []);

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const data = await invoiceApi.getInvoice(id);
        setInvoice(data);
        setCompanyInfo(data.companyInfo);
        setCustomerInfo(data.customerInfo);
        setInvoiceInfo(data.invoiceInfo);
        setLineItems(data.lineItems);
        setNotes(data.notes || 'Make cheque payable to "1001437133 ONTARIO INC."');
        setEmailTo(data.customerInfo?.email || "");
        setError(null);
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Invoice not found');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id]);

  const handleSendEmail = async () => {
    if (!emailTo) {
      toast.error("Please enter an email address");
      return;
    }
    
    try {
      setSendingEmail(true);
      await emailApi.sendInvoice(id, {
        to_email: emailTo,
        to_name: customerInfo?.name || "",
        message: emailMessage
      });
      toast.success("Invoice Sent!", {
        description: `Invoice emailed to ${emailTo}`
      });
      setEmailDialogOpen(false);
      setEmailMessage("");
    } catch (error) {
      toast.error("Failed to send email", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading invoice...</p>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <FileX className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Invoice Not Found</h1>
            <p className="text-muted-foreground mb-6">The invoice you&apos;re looking for doesn&apos;t exist or has been deleted.</p>
            <Button onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Invoices
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const updateCompanyInfo = (field, value) => {
    setCompanyInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateCustomerInfo = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateInvoiceInfoField = (field, value) => {
    setInvoiceInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateLineItem = (itemId, field, value) => {
    setLineItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const addLineItem = () => {
    const newId = Math.max(...lineItems.map(item => item.id), 0) + 1;
    setLineItems(prev => [
      ...prev,
      {
        id: newId,
        description: "New Item",
        unitPrice: 0,
        quantity: 1
      }
    ]);
  };

  const removeLineItem = (itemId) => {
    setLineItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleSave = async () => {
    try {
      await invoiceApi.updateInvoice(id, {
        companyInfo,
        customerInfo,
        invoiceInfo,
        lineItems,
        notes
      });
      
      // Update local invoice state
      setInvoice(prev => ({
        ...prev,
        companyInfo,
        customerInfo,
        invoiceInfo,
        lineItems,
        notes
      }));
      
      setIsEditing(false);
      toast.success("Invoice Saved", {
        description: `Invoice #${invoiceInfo.number} has been saved successfully.`
      });
    } catch (error) {
      toast.error("Failed to save invoice", {
        description: "Please try again."
      });
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setCompanyInfo(invoice.companyInfo);
    setCustomerInfo(invoice.customerInfo);
    setInvoiceInfo(invoice.invoiceInfo);
    setLineItems(invoice.lineItems);
    setNotes(invoice.notes || 'Make cheque payable to "1001437133 ONTARIO INC."');
    setIsEditing(false);
  };

  const handleSelectAgreement = (agreementId) => {
    const selectedAgreement = agreements.find(a => a.id === agreementId);
    if (selectedAgreement && selectedAgreement.renterInfo) {
      const renter = selectedAgreement.renterInfo;
      const insurance = selectedAgreement.insuranceInfo || {};
      setCustomerInfo({
        name: renter.name || '',
        address: renter.address || '',
        city: renter.city || '',
        province: renter.province || '',
        postalCode: renter.postalCode || '',
        phone: renter.phone || '',
        email: renter.email || '',
        claimNumber: customerInfo.claimNumber || '',
        insuranceCompany: insurance.insuranceCompany || '',
        policyNumber: insurance.policyNumber || ''
      });
      setEmailTo(renter.email || '');
      toast.success("Customer Imported", {
        description: `Customer info imported from agreement ${agreementId}`
      });
    }
  };

  const handleDownloadPDF = () => {
    const invoiceData = {
      companyInfo,
      customerInfo,
      invoiceInfo,
      lineItems
    };
    downloadInvoicePDF(invoiceData);
    toast.success("PDF Downloaded", {
      description: `Invoice ${invoiceInfo.number}.pdf has been downloaded.`
    });
  };

  const subtotal = lineItems.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  return (
    <div className="min-h-screen bg-background">
      {/* App Header - Hidden when printing */}
      <div className="no-print">
        <AppHeader />
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button & Action Buttons - Hidden when printing */}
        <div className="no-print mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Invoices
            </Button>
          </div>
          <InvoiceActions
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            invoiceInfo={invoiceInfo}
            companyInfo={companyInfo}
            customerInfo={customerInfo}
            lineItems={lineItems}
            subtotal={subtotal}
            tax={tax}
            total={total}
            onSave={handleSave}
            onCancel={handleCancel}
            onDownloadPDF={handleDownloadPDF}
            onPrintPDF={() => printInvoicePDF(invoice)}
            onSendEmail={() => setEmailDialogOpen(true)}
          />
        </div>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Send Invoice via Email
              </DialogTitle>
              <DialogDescription>
                Send invoice #{invoiceInfo.number} to the customer
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Recipient Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="customer@example.com"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  data-testid="email-to-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message (Optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Add a personal message to include in the email..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail} data-testid="send-email-confirm-btn">
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invoice
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice Container */}
        <div className="invoice-container animate-fade-in">
          {/* Invoice Header */}
          <InvoiceHeader
            companyInfo={companyInfo}
            invoiceInfo={invoiceInfo}
            isEditing={isEditing}
            updateCompanyInfo={updateCompanyInfo}
            updateInvoiceInfo={updateInvoiceInfoField}
          />

          {/* Invoice Body */}
          <div className="p-6 sm:p-8 space-y-8">
            {/* Company & Customer Details */}
            <InvoiceDetails
              companyInfo={companyInfo}
              customerInfo={customerInfo}
              invoiceInfo={invoiceInfo}
              isEditing={isEditing}
              updateCompanyInfo={updateCompanyInfo}
              updateCustomerInfo={updateCustomerInfo}
              updateInvoiceInfo={updateInvoiceInfoField}
              agreements={agreements}
              onSelectAgreement={handleSelectAgreement}
            />

            {/* Line Items Table */}
            <InvoiceLineItems
              lineItems={lineItems}
              isEditing={isEditing}
              updateLineItem={updateLineItem}
              addLineItem={addLineItem}
              removeLineItem={removeLineItem}
            />

            {/* Totals */}
            <InvoiceTotals
              subtotal={subtotal}
              tax={tax}
              taxRate={TAX_RATE}
              total={total}
            />

            {/* Notes Section */}
            <div className="border-t border-border pt-6">
              <Label className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3 block">
                Notes
              </Label>
              {isEditing ? (
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes or payment instructions..."
                  className="min-h-[80px] text-sm"
                />
              ) : (
                <div className="text-sm whitespace-pre-wrap bg-muted/30 p-4 rounded-lg">
                  <p className="text-red-600 font-semibold">
                    {notes || 'No notes'}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <InvoiceFooter companyInfo={companyInfo} />
          </div>
        </div>
      </main>
    </div>
  );
}
